import React, { useEffect, useState } from 'react';
import { Check, Loader2, Search } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import sessionsService from '../../services/sessionsServices';
import projectService from '../../services/projectService';
import Toast from '../Toast';
import { AddChatOverlay, AddChatModalCard } from './AddChatModal.style';

/**
 * Add existing chats to a project (dark overlay style).
 * API unchanged:
 *  - sessionsService.GetSessionsList
 *  - projectService.createProjectSessions
 */
const AddChatModal = ({ onClose, refetch }) => {
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const { id } = useParams();

  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const payload = {
    platform_id: 'gramielabs',
    user_id: user?.data?.ID,
    project_id: 'test',
  };
  const { sessions_loading, sessions_data } = sessionsService.GetSessionsList(payload);
  const [sessionData, setSessionData] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);

  const handleToggle = session => {
    setSelectedSessions(prev => {
      const exists = prev.some(item => item.session_id === session.id);
      if (exists) {
        return prev.filter(item => item.session_id !== session.id);
      }
      return [
        ...prev,
        {
          session_id: session?.id,
          initial_message: session?.initial_message,
          updated_at: session?.updated_at,
          title: session?.title,
        },
      ];
    });
  };

  const AddSession = async () => {
    if (selectedSessions.length === 0 || loading) return;
    setLoading(true);
    try {
      const resp = await projectService.createProjectSessions({
        project_id: id,
        sessions: selectedSessions,
      });
      refetch?.();
      onClose();
      Toast({ type: 'success', message: resp?.detail });
    } catch (error) {
      Toast({ type: 'error', message: error?.detail || 'Something went wrong' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessions_data) {
      function mergeSessions(sessions) {
        return Object.values(sessions)
          .flat()
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      }
      setSessionData(mergeSessions(sessions_data?.sessions));
    }
  }, [sessions_data]);

  const stripHtml = html => (html || '').replace(/<[^>]*>/g, '');
  const filteredSessions = sessionData.filter(elem => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      stripHtml(elem?.title).toLowerCase().includes(q) || stripHtml(elem?.initial_message).toLowerCase().includes(q)
    );
  });

  return (
    <AddChatOverlay onClick={onClose}>
      <AddChatModalCard onClick={e => e.stopPropagation()}>
        <div className="header">
          <h3>Add Chats</h3>
          <p className="subtitle">Select existing chats to add them to this project.</p>
          <div className="search">
            <Search size={16} />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search your chats"
            />
          </div>
        </div>

        <div className="list">
          {sessions_loading ? (
            <div className="loading">
              <Loader2 size={24} className="spin" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <p className="empty">No chats found.</p>
          ) : (
            filteredSessions.map((elem, ind) => {
              const selected = selectedSessions.some(item => item.session_id === elem.id);
              return (
                <button
                  key={elem?.id || ind}
                  type="button"
                  className={`session${selected ? ' selected' : ''}`}
                  onClick={() => handleToggle(elem)}>
                  <span className={`check${selected ? ' on' : ''}`}>{selected && <Check size={12} />}</span>
                  <span className="meta">
                    <strong className="title" dangerouslySetInnerHTML={{ __html: elem.title }} />
                    <span className="desc" dangerouslySetInnerHTML={{ __html: elem.initial_message }} />
                    <span className="date">{elem.updated_at}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="actions">
          <button type="button" className="cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="submit"
            onClick={AddSession}
            disabled={selectedSessions.length === 0 || loading}>
            {loading ? 'Adding...' : `Add Chats${selectedSessions.length > 0 ? ` (${selectedSessions.length})` : ''}`}
          </button>
        </div>
      </AddChatModalCard>
    </AddChatOverlay>
  );
};

export default AddChatModal;
